import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Divider,
  Avatar,
  Tab,
  Tabs,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  useTheme,
  Switch,
  FormControlLabel,
  styled,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import { tokens } from "../../theme";
import {
  CalendarMonth as CalendarIcon,
  PersonSearch as PersonSearchIcon,
  FileDownload as FileDownloadIcon,
  AccessTime as AccessTimeIcon,
  HourglassEmpty as HourglassEmptyIcon,
  HourglassFull as HourglassFullIcon,
  Person as PersonIcon,
  FilterAlt as FilterAltIcon,
  Sort as SortIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  EventBusy as EventBusyIcon
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO, startOfMonth, endOfMonth, isValid, differenceInDays, addDays, isAfter } from 'date-fns';
import { de } from 'date-fns/locale';
import axios from 'axios';
import { getToken, base_url } from '../../config/config';
import * as XLSX from 'xlsx';
import ReactToPrint from 'react-to-print';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
  transition: "transform 0.3s, box-shadow 0.3s",
  overflow: "visible",
  "&:hover": {
    boxShadow: "0 8px 30px 0 rgba(0,0,0,0.1)",
    transform: "translateY(-5px)"
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: "none",
  boxShadow: "none",
  fontWeight: 600,
  fontSize: 14,
  "&:hover": {
    boxShadow: "0 4px 10px 0 rgba(0,0,0,0.1)"
  }
}));

const FilterSection = styled(Box)(({ theme }) => ({
  padding: 16,
  borderRadius: 12,
  backgroundColor: theme.palette.mode === "dark" 
    ? theme.palette.background.paper 
    : "#fff",
  boxShadow: "0 2px 16px 0 rgba(0,0,0,0.05)",
  marginBottom: 24
}));

// Employee time evaluation component
const EmployeeHoursEvaluation = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const printRef = React.useRef();
  
  // State for API data
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track whether this is first load
  const [employees, setEmployees] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [projectData, setProjectData] = useState([]);
  const [alert, setAlert] = useState({ show: false, type: 'info', message: '' });
  
  // State for filters
  const [filters, setFilters] = useState({
    employee: "",
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date()),
    showOnlyOvertime: false,
    showOnlyNegativeHours: false
  });
  
  // State for display options
  const [activeTab, setActiveTab] = useState('summary');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  
  // State for summary data
  const [employeeSummary, setEmployeeSummary] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState({
    totalEmployees: 0,
    totalHours: 0,
    totalOvertime: 0,
    totalNegativeHours: 0
  });

  // Fetch all data on component mount
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        await fetchEmployees();
        await fetchAttendanceData();
        await fetchProjectData();
      } catch (error) {
        console.error("Error fetching initial data:", error);
        setAlert({
          show: true,
          type: 'error',
          message: 'Failed to load data. Please try again.'
        });
      } finally {
        setIsLoading(false);
        setIsInitialLoad(false);
        
        // Show a success message when component is ready
        setAlert({
          show: true,
          type: 'success',
          message: 'Arbeitszeitauswertung erfolgreich geladen. Bitte verwenden Sie die Filter, um die Daten anzupassen.'
        });
      }
    };
    
    fetchAllData();
  }, []);

  // Calculate summary when data or filters change
  useEffect(() => {
    if (employees.length > 0 && (attendanceData.length > 0 || projectData.length > 0)) {
      calculateEmployeeSummary();
    }
  }, [employees, attendanceData, projectData, filters]);
  
  // Force calculation on initial load to show data immediately
  useEffect(() => {
    if (!isLoading && employees.length > 0 && (attendanceData.length > 0 || projectData.length > 0)) {
      calculateEmployeeSummary();
    }
  }, [isLoading]);

  // Fetch employees from API
  const fetchEmployees = async () => {
    try {
      // Get auth token
      const token = getToken();
      
      const response = await axios.get(`${base_url}/v1/getAllEmployees/`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const employeeData = Array.isArray(response.data) ? response.data : 
                         (response.data && Array.isArray(response.data.data) ? response.data.data : []);
      
      if (employeeData.length > 0) {
        const formattedEmployees = employeeData.map(employee => ({
          id: employee._id,
          name: employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
          email: employee.email || '',
          role: employee.role || '',
          avatar: employee.avatar || ''
        }));
        setEmployees(formattedEmployees);
      } else {
        setAlert({
          show: true,
          type: 'warning',
          message: 'No employees found. Please add employees first.'
        });
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      throw error;
    }
  };
  
  // Fetch attendance data from API
  const fetchAttendanceData = async () => {
    try {
      // Format dates for API query
      const formattedStartDate = format(filters.startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(filters.endDate, 'yyyy-MM-dd');
      
      // Get auth token
      const token = getToken();
      
      // Adding query parameters and auth token
      const response = await axios.get(`${base_url}/v1/getDailyAttendance`, {
        params: {
          startDate: formattedStartDate,
          endDate: formattedEndDate
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data && response.data.data) {
        setAttendanceData(response.data.data);
        
        // Show alert if we got data
        if (response.data.data.length > 0) {
          setAlert({
            show: true,
            type: 'success',
            message: `${response.data.data.length} Anwesenheitseinträge geladen.`
          });
        }
      }
    } catch (error) {
      console.error("Error fetching attendance records:", error);
      throw error;
    }
  };
  
  // Fetch project data (for overtime) from API
  const fetchProjectData = async () => {
    try {
      // Format dates for API query
      const formattedStartDate = format(filters.startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(filters.endDate, 'yyyy-MM-dd');
      
      // Get auth token
      const token = getToken();
      
      // Adding query parameters and auth token
      const response = await axios.get(`${base_url}/v1/getAllUserProjects`, {
        params: {
          startDate: formattedStartDate,
          endDate: formattedEndDate
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data && response.data.status === 1) {
        setProjectData(response.data.data);
        
        // Count overtime projects
        const overtimeProjects = response.data.data.filter(project => project.isOverTime).length;
        if (overtimeProjects > 0) {
          setAlert({
            show: true,
            type: 'info',
            message: `${overtimeProjects} Überstunden-Projekte gefunden.`
          });
        }
      }
    } catch (error) {
      console.error("Error fetching project data:", error);
      throw error;
    }
  };

  // Function to calculate employee summary data based on filters
  const calculateEmployeeSummary = () => {
    let totalOvertime = 0;
    let totalNegativeHours = 0;
    let totalHours = 0;
    
    // Calculate summaries for each employee
    const summary = employees.map(employee => {
      // Filter attendance records for this employee
      const employeeAttendance = attendanceData.filter(record => {
        const recordDate = new Date(record.Date || record.createdAt);
        return (
          record.user === employee.name &&
          recordDate >= filters.startDate &&
          recordDate <= filters.endDate
        );
      });
      
      // Filter project records with overtime for this employee
      const employeeProjects = projectData.filter(project => {
        const projectDate = new Date(project.date);
        return (
          project.user === employee.name &&
          project.isOverTime === true &&
          projectDate >= filters.startDate &&
          projectDate <= filters.endDate
        );
      });
      
      // Calculate regular hours from attendance
      let regularHours = 0;
      let overtimeHours = 0;
      let negativeHours = 0;
      
      employeeAttendance.forEach(record => {
        if (!record.Abwesend && record.StartTime && record.endTime) {
          // Parse start and end times
          const startParts = record.StartTime.split(':');
          const endParts = record.endTime.split(':');
          
          if (startParts.length >= 2 && endParts.length >= 2) {
            const startHour = parseInt(startParts[0]);
            const startMin = parseInt(startParts[1]);
            const endHour = parseInt(endParts[0]);
            const endMin = parseInt(endParts[1]);
            
            // Calculate hours as a decimal
            let hours = (endHour - startHour) + ((endMin - startMin) / 60);
            
            // Apply break time deductions (15 mins morning, 15 mins noon)
            if (hours > 0) {
              if (startHour <= 9 && endHour >= 9) {
                hours -= 0.25; // Morning break
              }
              
              if (startHour <= 12 && endHour >= 12) {
                hours -= 0.25; // Noon break
              }
            }
            
            if (hours > 0) {
              regularHours += hours;
            }
          }
        }
      });
      
      // Calculate overtime from projects
      employeeProjects.forEach(project => {
        if (project.StartTime && project.EndTime) {
          const startParts = project.StartTime.split(':');
          const endParts = project.EndTime.split(':');
          
          if (startParts.length >= 2 && endParts.length >= 2) {
            const startHour = parseInt(startParts[0]);
            const startMin = parseInt(startParts[1]);
            const endHour = parseInt(endParts[0]);
            const endMin = parseInt(endParts[1]);
            
            // Calculate hours as a decimal
            const hours = (endHour - startHour) + ((endMin - startMin) / 60);
            
            if (hours > 0) {
              overtimeHours += hours;
            }
          }
        }
      });
      
      // Calculate expected hours for the date range (8 hours per workday)
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      const totalDays = differenceInDays(endDate, startDate) + 1;
      
      // Count workdays (excluding weekends)
      let workdays = 0;
      let currentDate = new Date(startDate);
      
      for (let i = 0; i < totalDays; i++) {
        const dayOfWeek = currentDate.getDay();
        // 0 is Sunday, 6 is Saturday
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          workdays++;
        }
        currentDate = addDays(currentDate, 1);
      }
      
      const expectedHours = workdays * 8;
      
      // Calculate negative hours if actual < expected
      if (regularHours < expectedHours) {
        negativeHours = expectedHours - regularHours;
      }
      
      // Update totals for stats
      totalHours += regularHours;
      totalOvertime += overtimeHours;
      totalNegativeHours += negativeHours;
      
      return {
        id: employee.id,
        name: employee.name,
        regularHours: regularHours.toFixed(2),
        expectedHours: expectedHours.toFixed(2),
        overtimeHours: overtimeHours.toFixed(2),
        negativeHours: negativeHours.toFixed(2),
        balance: (regularHours + overtimeHours - expectedHours).toFixed(2),
        attendanceDays: employeeAttendance.filter(record => !record.Abwesend).length,
        absenceDays: employeeAttendance.filter(record => record.Abwesend).length,
        overtimeProjects: employeeProjects.length
      };
    });
    
    // Filter summary based on user filters
    let filteredSummary = summary;
    
    if (filters.employee) {
      filteredSummary = filteredSummary.filter(item => 
        item.name.toLowerCase().includes(filters.employee.toLowerCase())
      );
    }
    
    if (filters.showOnlyOvertime) {
      filteredSummary = filteredSummary.filter(item => 
        parseFloat(item.overtimeHours) > 0
      );
    }
    
    if (filters.showOnlyNegativeHours) {
      filteredSummary = filteredSummary.filter(item => 
        parseFloat(item.negativeHours) > 0
      );
    }
    
    // If we have no results after filtering, show all employees
    if (filteredSummary.length === 0 && summary.length > 0) {
      filteredSummary = summary;
      
      // Show a notification that filters were automatically reset
      setAlert({
        show: true,
        type: 'info',
        message: 'Filter wurden zurückgesetzt, um alle verfügbaren Daten anzuzeigen.'
      });
    }
    
    // Sort the summary data
    filteredSummary.sort((a, b) => {
      if (sortConfig.key === 'name') {
        const comparison = a.name.localeCompare(b.name);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      } else {
        const valA = parseFloat(a[sortConfig.key]);
        const valB = parseFloat(b[sortConfig.key]);
        const comparison = valA - valB;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }
    });
    
    // Update state with calculated data
    setEmployeeSummary(filteredSummary);
    setMonthlyStats({
      totalEmployees: filteredSummary.length,
      totalHours: totalHours.toFixed(2),
      totalOvertime: totalOvertime.toFixed(2),
      totalNegativeHours: totalNegativeHours.toFixed(2)
    });
  };
  
  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Immediately refetch data when date filters change
    if (field === 'startDate' || field === 'endDate') {
      setIsLoading(true);
      fetchAttendanceData();
      fetchProjectData();
    }
  };
  
  // Handle sort changes
  const handleSort = (column) => {
    setSortConfig(prev => ({
      key: column,
      direction: prev.key === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  // Reset all filters
  const handleResetFilters = () => {
    setFilters({
      employee: "",
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
      showOnlyOvertime: false,
      showOnlyNegativeHours: false
    });
  };
  
  // Export to Excel
  const handleExportExcel = () => {
    try {
      const workbook = XLSX.utils.book_new();
      
      // Main summary worksheet
      const summaryData = employeeSummary.map(employee => ({
        'Mitarbeiter': employee.name,
        'Reguläre Stunden': employee.regularHours,
        'Überstunden': employee.overtimeHours,
        'Minusstunden': employee.negativeHours,
        'Erwartete Stunden': employee.expectedHours,
        'Bilanz': employee.balance,
        'Anwesenheitstage': employee.attendanceDays,
        'Abwesenheitstage': employee.absenceDays,
        'Überstunden-Projekte': employee.overtimeProjects
      }));
      
      const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Mitarbeiter Zusammenfassung");
      
      // Export per employee
      if (filters.employee) {
        const employee = employeeSummary.find(emp => 
          emp.name.toLowerCase().includes(filters.employee.toLowerCase())
        );
        
        if (employee) {
          // Get detailed attendance for this employee
          const employeeAttendance = attendanceData.filter(record => {
            const recordDate = new Date(record.Date || record.createdAt);
            return (
              record.user === employee.name &&
              recordDate >= filters.startDate &&
              recordDate <= filters.endDate
            );
          });
          
          const attendanceData = employeeAttendance.map(record => ({
            'Datum': record.Date ? format(new Date(record.Date), 'dd.MM.yyyy') : 
                     format(new Date(record.createdAt), 'dd.MM.yyyy'),
            'Startzeit': record.StartTime || '-',
            'Endzeit': record.endTime || '-',
            'Abwesend': record.Abwesend ? 'Ja' : 'Nein',
            'Abwesenheitstyp': record.AbwesendType || '-'
          }));
          
          const attendanceWorksheet = XLSX.utils.json_to_sheet(attendanceData);
          XLSX.utils.book_append_sheet(workbook, attendanceWorksheet, `${employee.name} Anwesenheit`);
          
          // Get overtime projects for this employee
          const employeeProjects = projectData.filter(project => {
            const projectDate = new Date(project.date);
            return (
              project.user === employee.name &&
              project.isOverTime === true &&
              projectDate >= filters.startDate &&
              projectDate <= filters.endDate
            );
          });
          
          const projectsData = employeeProjects.map(project => ({
            'Datum': format(new Date(project.date), 'dd.MM.yyyy'),
            'ANR': project.ANR || '-',
            'Beschreibung': project.description || '-',
            'Startzeit': project.StartTime || '-',
            'Endzeit': project.EndTime || '-',
            'Überstundenstatus': project.UberStundenStatus || 'pending'
          }));
          
          const projectsWorksheet = XLSX.utils.json_to_sheet(projectsData);
          XLSX.utils.book_append_sheet(workbook, projectsWorksheet, `${employee.name} Überstunden`);
        }
      }
      
      // Define file name with date range
      const startFormatted = format(filters.startDate, 'yyyy-MM-dd');
      const endFormatted = format(filters.endDate, 'yyyy-MM-dd');
      const fileName = `Mitarbeiter_Stunden_${startFormatted}_bis_${endFormatted}.xlsx`;
      
      // Write the file
      XLSX.writeFile(workbook, fileName);
      
      setAlert({
        show: true,
        type: 'success',
        message: 'Daten erfolgreich als Excel exportiert'
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      setAlert({
        show: true,
        type: 'error',
        message: 'Fehler beim Exportieren nach Excel'
      });
    }
  };
  
  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      <Box sx={{ p: 2 }}>
        {/* Page header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h3" fontWeight="bold" color={colors.grey[100]}>
            Mitarbeiter Arbeitszeitauswertung
          </Typography>
        </Box>
        
        {/* Alert messages */}
        {alert.show && (
          <Alert 
            severity={alert.type} 
            sx={{ mb: 2 }}
            onClose={() => setAlert({ ...alert, show: false })}
          >
            {alert.message}
          </Alert>
        )}
        
        {/* Loading indicator */}
        {(isLoading || employeeSummary.length === 0) && <LinearProgress sx={{ mb: 2 }} />}
        
        {/* Stats cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent>
                <Stack spacing={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight="bold" color={colors.greenAccent[500]}>
                      Mitarbeiter
                    </Typography>
                    <Avatar sx={{ bgcolor: colors.greenAccent[500] }}>
                      <PersonIcon />
                    </Avatar>
                  </Box>
                  <Typography variant="h3" fontWeight="bold">
                    {monthlyStats.totalEmployees}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[400]}>
                    Aktive Mitarbeiter
                  </Typography>
                </Stack>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent>
                <Stack spacing={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight="bold" color={colors.blueAccent[500]}>
                      Arbeitsstunden
                    </Typography>
                    <Avatar sx={{ bgcolor: colors.blueAccent[500] }}>
                      <AccessTimeIcon />
                    </Avatar>
                  </Box>
                  <Typography variant="h3" fontWeight="bold">
                    {monthlyStats.totalHours}h
                  </Typography>
                  <Typography variant="body2" color={colors.grey[400]}>
                    Gesamte Arbeitsstunden
                  </Typography>
                </Stack>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent>
                <Stack spacing={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight="bold" color={colors.redAccent[500]}>
                      Überstunden
                    </Typography>
                    <Avatar sx={{ bgcolor: colors.redAccent[500] }}>
                      <HourglassFullIcon />
                    </Avatar>
                  </Box>
                  <Typography variant="h3" fontWeight="bold">
                    {monthlyStats.totalOvertime}h
                  </Typography>
                  <Typography variant="body2" color={colors.grey[400]}>
                    Gesamte Überstunden
                  </Typography>
                </Stack>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent>
                <Stack spacing={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight="bold" color={colors.primary[500]}>
                      Minusstunden
                    </Typography>
                    <Avatar sx={{ bgcolor: colors.primary[500] }}>
                      <HourglassEmptyIcon />
                    </Avatar>
                  </Box>
                  <Typography variant="h3" fontWeight="bold">
                    {monthlyStats.totalNegativeHours}h
                  </Typography>
                  <Typography variant="body2" color={colors.grey[400]}>
                    Gesamte Minusstunden
                  </Typography>
                </Stack>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
        
        {/* Filters Section */}
        <FilterSection>
          <Box display="flex" alignItems="center" mb={1}>
            <FilterAltIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Filter</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title="Filter zurücksetzen">
              <IconButton onClick={handleResetFilters} color="secondary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label="Mitarbeiter suchen"
                fullWidth
                value={filters.employee}
                onChange={(e) => handleFilterChange('employee', e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonSearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Von Datum"
                value={filters.startDate}
                onChange={(newValue) => {
                  if (newValue && isValid(newValue)) {
                    handleFilterChange('startDate', newValue);
                  }
                }}
                slotProps={{ textField: { 
                  size: 'small', 
                  fullWidth: true,
                  InputProps: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon />
                      </InputAdornment>
                    )
                  }
                }}}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Bis Datum"
                value={filters.endDate}
                onChange={(newValue) => {
                  if (newValue && isValid(newValue)) {
                    handleFilterChange('endDate', newValue);
                  }
                }}
                slotProps={{ textField: { 
                  size: 'small', 
                  fullWidth: true,
                  InputProps: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon />
                      </InputAdornment>
                    )
                  }
                }}}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.showOnlyOvertime}
                      onChange={(e) => handleFilterChange('showOnlyOvertime', e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label="Nur Überstunden"
                  labelPlacement="end"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.showOnlyNegativeHours}
                      onChange={(e) => handleFilterChange('showOnlyNegativeHours', e.target.checked)}
                      color="secondary"
                      size="small"
                    />
                  }
                  label="Nur Minusstunden"
                  labelPlacement="end"
                />
              </Box>
            </Grid>
          </Grid>
        </FilterSection>
        
        {/* Action Buttons */}
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab value="summary" label="Zusammenfassung" />
            <Tab value="detail" label="Detailansicht" />
          </Tabs>
          
          <Box display="flex" gap={2}>
            <ActionButton
              variant="outlined"
              color="primary"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportExcel}
              disabled={employeeSummary.length === 0}
            >
              Excel Export
            </ActionButton>
            
            <ReactToPrint
              trigger={() => (
                <ActionButton
                  variant="outlined"
                  color="secondary"
                  startIcon={<PrintIcon />}
                  disabled={employeeSummary.length === 0}
                >
                  Drucken
                </ActionButton>
              )}
              content={() => printRef.current}
            />
          </Box>
        </Box>
        
        {/* Data display section - ref for printing */}
        <Box ref={printRef} sx={{ position: 'relative' }}>
          {employeeSummary.length === 0 && !isLoading && !isInitialLoad ? (
            <Paper 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3
              }}
            >
              <EventBusyIcon sx={{ fontSize: 60, color: colors.grey[500], mb: 2, opacity: 0.5 }} />
              <Typography variant="h5" color={colors.grey[500]}>
                Daten werden geladen...
              </Typography>
              <Typography variant="body2" color={colors.grey[400]} sx={{ mt: 1 }}>
                Wenn keine Daten erscheinen, überprüfen Sie bitte, ob Anwesenheitseinträge im System vorhanden sind.
              </Typography>
            </Paper>
          ) : (
            <>
              {activeTab === 'summary' ? (
                <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                  <Box sx={{ p: 2, borderBottom: `1px solid ${colors.grey[800]}` }}>
                    <Typography variant="h6" fontWeight="bold">
                      Arbeitszeitauswertung - {format(filters.startDate, 'dd.MM.yyyy')} bis {format(filters.endDate, 'dd.MM.yyyy')}
                    </Typography>
                  </Box>
                  
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: colors.primary[400] }}>
                        <TableCell 
                          onClick={() => handleSort('name')}
                          sx={{ 
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            '&:hover': { backgroundColor: colors.primary[300] }
                          }}
                        >
                          <Box display="flex" alignItems="center">
                            Mitarbeiter
                            {sortConfig.key === 'name' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        
                        <TableCell 
                          onClick={() => handleSort('regularHours')}
                          align="right"
                          sx={{ 
                            cursor: 'pointer', 
                            fontWeight: 'bold',
                            '&:hover': { backgroundColor: colors.primary[300] }
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="flex-end">
                            Reguläre Stunden
                            {sortConfig.key === 'regularHours' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        
                        <TableCell 
                          onClick={() => handleSort('expectedHours')}
                          align="right"
                          sx={{ 
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            '&:hover': { backgroundColor: colors.primary[300] }
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="flex-end">
                            Erwartete Stunden
                            {sortConfig.key === 'expectedHours' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        
                        <TableCell 
                          onClick={() => handleSort('overtimeHours')}
                          align="right"
                          sx={{ 
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            '&:hover': { backgroundColor: colors.primary[300] }
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="flex-end">
                            Überstunden
                            {sortConfig.key === 'overtimeHours' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        
                        <TableCell 
                          onClick={() => handleSort('negativeHours')}
                          align="right"
                          sx={{ 
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            '&:hover': { backgroundColor: colors.primary[300] }
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="flex-end">
                            Minusstunden
                            {sortConfig.key === 'negativeHours' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        
                        <TableCell 
                          onClick={() => handleSort('balance')}
                          align="right"
                          sx={{ 
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            '&:hover': { backgroundColor: colors.primary[300] }
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="flex-end">
                            Bilanz
                            {sortConfig.key === 'balance' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    
                    <TableBody>
                      {employeeSummary
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((employee) => (
                          <TableRow key={employee.id} hover>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar 
                                  sx={{ 
                                    width: 30, 
                                    height: 30, 
                                    bgcolor: colors.blueAccent[500],
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  {employee.name?.charAt(0).toUpperCase() || "?"}
                                </Avatar>
                                <Typography>{employee.name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">{employee.regularHours}h</TableCell>
                            <TableCell align="right">{employee.expectedHours}h</TableCell>
                            <TableCell align="right">
                              {parseFloat(employee.overtimeHours) > 0 ? (
                                <Chip 
                                  label={`${employee.overtimeHours}h`} 
                                  color="error" 
                                  size="small"
                                  variant={parseFloat(employee.overtimeHours) > 5 ? "filled" : "outlined"}
                                />
                              ) : (
                                '0.00h'
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {parseFloat(employee.negativeHours) > 0 ? (
                                <Chip 
                                  label={`${employee.negativeHours}h`} 
                                  color="primary" 
                                  size="small"
                                  variant={parseFloat(employee.negativeHours) > 5 ? "filled" : "outlined"}
                                />
                              ) : (
                                '0.00h'
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                sx={{
                                  color: parseFloat(employee.balance) >= 0 ? 
                                    colors.greenAccent[500] : colors.redAccent[500],
                                  fontWeight: 'bold'
                                }}
                              >
                                {employee.balance}h
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  
                  <TablePagination
                    component="div"
                    count={employeeSummary.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Zeilen pro Seite:"
                    sx={{ borderTop: `1px solid ${colors.grey[800]}` }}
                  />
                </TableContainer>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                  <Box sx={{ p: 2, borderBottom: `1px solid ${colors.grey[800]}` }}>
                    <Typography variant="h6" fontWeight="bold">
                      Detaillierte Arbeitszeitauswertung - {format(filters.startDate, 'dd.MM.yyyy')} bis {format(filters.endDate, 'dd.MM.yyyy')}
                    </Typography>
                  </Box>
                  
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: colors.primary[400] }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Mitarbeiter</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Anwesenheitstage</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Abwesenheitstage</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Überstunden-Projekte</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Reguläre Stunden</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Überstunden</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Minusstunden</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Bilanz</TableCell>
                      </TableRow>
                    </TableHead>
                    
                    <TableBody>
                      {employeeSummary
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((employee) => (
                          <TableRow key={employee.id} hover>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar 
                                  sx={{ 
                                    width: 30, 
                                    height: 30, 
                                    bgcolor: colors.blueAccent[500],
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  {employee.name?.charAt(0).toUpperCase() || "?"}
                                </Avatar>
                                <Typography>{employee.name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">{employee.attendanceDays}</TableCell>
                            <TableCell align="right">
                              {employee.absenceDays > 0 ? (
                                <Chip 
                                  label={employee.absenceDays} 
                                  color="warning" 
                                  size="small"
                                />
                              ) : (
                                '0'
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {employee.overtimeProjects > 0 ? (
                                <Chip 
                                  label={employee.overtimeProjects} 
                                  color="error" 
                                  size="small"
                                />
                              ) : (
                                '0'
                              )}
                            </TableCell>
                            <TableCell align="right">{employee.regularHours}h</TableCell>
                            <TableCell align="right">
                              {parseFloat(employee.overtimeHours) > 0 ? (
                                <Typography color="error.main" fontWeight="bold">
                                  {employee.overtimeHours}h
                                </Typography>
                              ) : (
                                '0.00h'
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {parseFloat(employee.negativeHours) > 0 ? (
                                <Typography color="primary.main" fontWeight="bold">
                                  {employee.negativeHours}h
                                </Typography>
                              ) : (
                                '0.00h'
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                sx={{
                                  color: parseFloat(employee.balance) >= 0 ? 
                                    colors.greenAccent[500] : colors.redAccent[500],
                                  fontWeight: 'bold'
                                }}
                              >
                                {employee.balance}h
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  
                  <TablePagination
                    component="div"
                    count={employeeSummary.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Zeilen pro Seite:"
                    sx={{ borderTop: `1px solid ${colors.grey[800]}` }}
                  />
                </TableContainer>
              )}
            </>
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default EmployeeHoursEvaluation;